<template>
  <cs-form-dialog
    id="dlg-hub-config-form"
    v-model="dialogVisible"
    body-id="dlg-hub-config-form-body"
    :heading="isEditing ? 'Edit Hub Configuration' : 'Assign Hub to Client'"
    :primary-action="{
      label: isEditing ? 'Update' : 'Assign',
      disabled: !allowSave
    }"
    :secondary-action1="{
      label: 'Cancel'
    }"
    @primary-click="onSave"
    @secondary1-click="onCancel"
  >
    <template #cs-form-dialog-content>
      <v-row class="pt-4">
        <v-col cols="12">
          <v-autocomplete
            v-if="!isEditing"
            id="client-hub-config-edit-dialog-selHub"
            v-model="selectedHub"
            :items="hubList"
            label="Select Hub"
            placeholder="Search for a Hub"
            density="compact"
            :error-messages="selectedHubErrors"
            @update:model-value="onSelectedHubChange"
            @blur="v$.selectedHub.$touch()"
          />
        </v-col>
      </v-row>
      <hub-config-form
        ref="hubConfigForm"
        v-model="hubConfigData"
        :disabled="pageLoading"
        @update:valid="onHubConfigFormValidChange"
      />
    </template>
  </cs-form-dialog>
  <cs-form-dialog
    id="dlg-client-hub-config-edit-save-confirmation"
    v-model="showOverwriteConfirmationDialog"
    heading="Overwrite Confirmation"
    body-id="dlg-client-hub-config-edit-save-confirmation-body"
    :primary-action="{
      label: 'Overwrite',
      eventName: 'primary-click'
    }"
    :secondary-action1="{
      label: 'Cancel',
      eventName: 'secondary1-click'
    }"
    @primary-click="onOverwriteClick()"
    @secondary1-click="showOverwriteConfirmationDialog = false"
  >
    <template #cs-form-dialog-content>
      <v-container class="pa-0">
        <v-row no-gutters>
          <v-col cols="12">
            <div>
              <span>
                Hub Configuration is not empty. Do you want to overwrite the existing tenant?
              </span>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </cs-form-dialog>
</template>

<script>
import { CSBase } from '@complispace/cs-design-system';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import HubConfigForm from './HubConfigForm';

export default {
  name: 'ClientHubConfigEditDialog',

  components: {
    HubConfigForm
  },

  extends: CSBase,

  props: {
    modelValue: { type: Boolean, required: true },
    isEditing: { type: Boolean, default: false },
    hubList: { type: Array, default: () => [] },
    hubData: { type: Object, default: () => ({}) },
    clientSid: { type: String, default: '' }
  },

  emits: ['update:modelValue', 'save'],

  setup: () => ({ v$: useVuelidate() }),

  data() {
    return {
      selectedHub: '',
      hubConfigFormValid: false,
      hubConfigData: {
        displayName: '',
        userPoolClientId: '',
        organisationId: '',
        userPoolId: '',
        cognitoDomain: ''
      },
      showOverwriteConfirmationDialog: false,
      pageLoading: false
    };
  },

  validations: {
    selectedHub: { required }
  },

  computed: {
    dialogVisible: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      }
    },
    selectedHubErrors() {
      const errors = [];
      if (!this.v$.selectedHub || !this.v$.selectedHub.$dirty) return errors;
      if (this.v$.selectedHub.required && this.v$.selectedHub.required.$invalid) {
        errors.push('Hub selection is required');
      }
      return errors;
    },
    allowSave() {
      const hubSelected = this.isEditing || !this.v$.selectedHub.$invalid;
      return this.hubConfigFormValid && hubSelected && !this.pageLoading;
    },
    hubConfigEdited() {
      return (
        this.hubConfigData.displayName !== '' ||
        this.hubConfigData.organisationId !== '' ||
        this.hubConfigData.userPoolId !== '' ||
        this.hubConfigData.userPoolClientId !== '' ||
        this.hubConfigData.cognitoDomain !== ''
      );
    }
  },

  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.populateForm();
      }
    }
  },

  methods: {
    async fetchHubConfig(hubKey) {
      try {
        this.pageLoading = true;
        const response = await this.$store.dispatch('hub-integration/getHubTenantHubConfig', {
          displayName: hubKey
        });
        if (response) {
          this.hubConfigData = {
            displayName: response.displayName || '',
            organisationId: response.organisationId || '',
            userPoolId: response.userPoolId || '',
            userPoolClientId: response.userPoolClientId || '',
            cognitoDomain: response.cognitoDomain || ''
          };
        }
      } catch {
        // keep form empty on error
      } finally {
        this.pageLoading = false;
      }
    },
    populateForm() {
      if (this.isEditing && this.hubData) {
        this.hubConfigData = {
          displayName: this.hubData.displayName || '',
          organisationId: this.hubData.organisationId || '',
          userPoolId: this.hubData.userPoolId || '',
          userPoolClientId: this.hubData.userPoolClientId || '',
          cognitoDomain: this.hubData.cognitoDomain || ''
        };
      } else {
        this.hubConfigData = {
          displayName: '',
          organisationId: '',
          userPoolId: '',
          userPoolClientId: '',
          cognitoDomain: ''
        };
        this.selectedHub = '';
      }
      this.v$.$reset();
      if (this.$refs.hubConfigForm) {
        this.$refs.hubConfigForm.resetValidation();
      }
    },
    async onSelectedHubChange(val) {
      this.selectedHub = val;
      this.v$.selectedHub.$touch();
      if (this.hubConfigEdited && val) {
        this.showOverwriteConfirmationDialog = true;
        return;
      }
      if (val && !this.isEditing) {
        await this.fetchHubConfig(val);
      }
    },
    async onOverwriteClick() {
      this.showOverwriteConfirmationDialog = false;
      await this.fetchHubConfig(this.selectedHub);
    },
    onHubConfigFormValidChange(valid) {
      this.hubConfigFormValid = valid;
    },
    async onSave() {
      const payload = {
        ...this.hubConfigData
      };
      this.$emit('save', payload);
      this.$emit('update:modelValue', false);
    },

    onCancel() {
      this.$emit('update:modelValue', false);
    }
  }
};
</script>
